/**
 * Definicje błędów dla funkcjonalności Music Attachments (frontend)
 * 
 * Zapewnia spójność z backendem i umożliwia rozróżnienie typów błędów
 */

export enum MusicUploadErrorType {
  /**
   * Błąd podczas generowania Signed URL w backendzie
   */
  URL_GENERATION_ERROR = "URL_GENERATION_ERROR",

  /**
   * Błąd podczas uploadu pliku do Google Cloud Storage
   */
  UPLOAD_ERROR = "UPLOAD_ERROR",

  /**
   * Błąd podczas przetwarzania pliku w backendzie (Storage trigger)
   */
  PROCESSING_ERROR = "PROCESSING_ERROR",

  /**
   * Błąd walidacji (pliku, uprawnień, itp.)
   */
  VALIDATION_ERROR = "VALIDATION_ERROR",

  /**
   * Błąd sieciowy (timeout, brak połączenia)
   */
  NETWORK_ERROR = "NETWORK_ERROR",

  /**
   * Nieznany błąd
   */
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
}

/**
 * Klasa błędu dla operacji uploadu muzyki
 */
export class MusicUploadError extends Error {
  constructor(
    public type: MusicUploadErrorType,
    message: string,
    public originalError?: Error | unknown,
    public details?: Record<string, any>
  ) {
    super(message);
    this.name = "MusicUploadError";
  }

  /**
   * Sprawdza czy błąd jest typu sieciowego (można retry)
   */
  isRetryable(): boolean {
    return (
      this.type === MusicUploadErrorType.NETWORK_ERROR ||
      this.type === MusicUploadErrorType.UPLOAD_ERROR
    );
  }

  /**
   * Sprawdza czy błąd jest typu walidacyjnego (nie można retry)
   */
  isValidationError(): boolean {
    return this.type === MusicUploadErrorType.VALIDATION_ERROR;
  }
}

/**
 * Tworzy MusicUploadError na podstawie błędu z backendu
 */
export function createMusicUploadErrorFromBackend(
  error: unknown,
  context?: { step: string; [key: string]: any }
): MusicUploadError {
  if (error instanceof MusicUploadError) {
    return error;
  }

  // Sprawdź czy to FirebaseError z kodem
  if (error && typeof error === "object" && "code" in error) {
    const firebaseError = error as { code: string; message: string };
    
    // Mapowanie kodów Firebase na typy błędów
    if (firebaseError.code === "failed-precondition") {
      return new MusicUploadError(
        MusicUploadErrorType.VALIDATION_ERROR,
        firebaseError.message || "Music upload is locked for this contest",
        error,
        context
      );
    }

    if (firebaseError.code === "invalid-argument" || firebaseError.code === "permission-denied") {
      return new MusicUploadError(
        MusicUploadErrorType.VALIDATION_ERROR,
        firebaseError.message || "Validation error",
        error,
        context
      );
    }

    if (firebaseError.code === "unauthenticated" || firebaseError.code === "permission-denied") {
      return new MusicUploadError(
        MusicUploadErrorType.VALIDATION_ERROR,
        firebaseError.message || "Authentication error",
        error,
        context
      );
    }

    if (firebaseError.code === "internal" || firebaseError.code === "unavailable") {
      return new MusicUploadError(
        MusicUploadErrorType.URL_GENERATION_ERROR,
        firebaseError.message || "Backend error",
        error,
        context
      );
    }
  }

  // Sprawdź czy to błąd sieciowy (fetch)
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return new MusicUploadError(
      MusicUploadErrorType.NETWORK_ERROR,
      "Network error during upload",
      error,
      context
    );
  }

  // Domyślny błąd
  return new MusicUploadError(
    MusicUploadErrorType.UNKNOWN_ERROR,
    error instanceof Error ? error.message : "Unknown error",
    error,
    context
  );
}

/**
 * Tworzy MusicUploadError na podstawie błędu uploadu do GCS
 */
export function createMusicUploadErrorFromUpload(
  status: number,
  statusText: string,
  errorText?: string,
  context?: { step: string; [key: string]: any }
): MusicUploadError {
  // Błędy 4xx - walidacja
  if (status >= 400 && status < 500) {
    return new MusicUploadError(
      MusicUploadErrorType.VALIDATION_ERROR,
      `Upload validation error: ${status} ${statusText}. ${errorText || ""}`,
      undefined,
      { ...context, status, statusText, errorText }
    );
  }

  // Błędy 5xx - błąd serwera
  if (status >= 500) {
    return new MusicUploadError(
      MusicUploadErrorType.UPLOAD_ERROR,
      `Upload server error: ${status} ${statusText}. ${errorText || ""}`,
      undefined,
      { ...context, status, statusText, errorText }
    );
  }

  // Inne błędy
  return new MusicUploadError(
    MusicUploadErrorType.UPLOAD_ERROR,
    `Upload error: ${status} ${statusText}. ${errorText || ""}`,
    undefined,
    { ...context, status, statusText, errorText }
  );
}
